import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './SEOContent.css';

const SEOContent = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const dentalTreatments = [
    {
      title: "Root Canal Treatment in Bhaktapur",
      description: "Advanced painless root canal therapy using modern endodontic techniques. Save your natural teeth with our expert endodontists.",
      keywords: ["root canal Bhaktapur", "endodontic treatment", "painless root canal"]
    },
    {
      title: "Dental Implants Bhaktapur",
      description: "Permanent tooth replacement with titanium dental implants. Restore your smile with our experienced oral surgeons.",
      keywords: ["dental implants Bhaktapur", "tooth replacement", "oral surgery"]
    },
    {
      title: "Braces and Orthodontics",
      description: "Straighten your teeth with traditional braces, clear aligners, and modern orthodontic treatments for all ages.",
      keywords: ["braces Bhaktapur", "orthodontics", "teeth straightening"]
    },
    {
      title: "Cosmetic Dentistry Services",
      description: "Transform your smile with teeth whitening, veneers, bonding, and other aesthetic dental procedures.",
      keywords: ["cosmetic dentistry Bhaktapur", "teeth whitening", "dental veneers"]
    }
  ];

  const serviceAreas = [
    "Bhaktapur", "Suryabinayak", "Katunje", "Madhyapur Thimi", "Changunarayan",
    "Nagarkot", "Bode", "Sipadol", "Balkot", "Dattatreya"
  ];

  return (
    <section className="seo-content" ref={ref}>
      <div className="container">
        {/* Dental Treatments Section */}
        <motion.div 
          className="treatments-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2>Comprehensive Dental Treatment in Bhaktapur</h2>
          <p className="section-intro">
            Premier Dental Clinic offers the most advanced dental treatments in Bhaktapur, 
            combining modern technology with experienced dental professionals to provide 
            exceptional oral healthcare services.
          </p>
          
          <div className="treatments-grid">
            {dentalTreatments.map((treatment, index) => (
              <motion.article
                key={index}
                className="treatment-card"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <h3>{treatment.title}</h3>
                <p>{treatment.description}</p>
                <div className="keywords">
                  {treatment.keywords.map((keyword, i) => (
                    <span key={i} className="keyword-tag">{keyword}</span>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>

        {/* Service Areas Section */}
        <motion.div 
          className="service-areas-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <h2>Serving Dental Patients Across Bhaktapur District</h2>
          <p>
            Our dental clinic is conveniently located in Suryabinayak, making us easily 
            accessible to patients from all areas of Bhaktapur district. We proudly serve 
            the dental healthcare needs of the following communities:
          </p>
          
          <div className="service-areas-grid">
            {serviceAreas.map((area, index) => (
              <motion.div
                key={index}
                className="area-tag"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                📍 {area}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section for SEO */}
        <motion.div 
          className="faq-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <h2>Frequently Asked Questions - Dental Treatment Bhaktapur</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Why is Premier Dental Clinic the best dental clinic in Bhaktapur?</h3>
              <p>
                We combine modern dental technology, international hygiene standards, 
                experienced dentists, and painless treatment techniques to provide 
                exceptional dental care in a comfortable environment.
              </p>
            </div>
            
            <div className="faq-item">
              <h3>What dental treatments are available in Bhaktapur?</h3>
              <p>
                We offer comprehensive dental services including root canal treatment, 
                dental implants, braces, cosmetic dentistry, teeth cleaning, oral surgery, 
                and full-mouth restoration.
              </p>
            </div>
            
            <div className="faq-item">
              <h3>Do you provide painless dental treatment?</h3>
              <p>
                Yes, we specialize in painless dental procedures using advanced anesthesia 
                techniques and modern equipment to ensure patient comfort during all treatments.
              </p>
            </div>
            
            <div className="faq-item">
              <h3>How to book dental appointment in Bhaktapur?</h3>
              <p>
                You can book an appointment by calling +977 981-3538101 or using our 
                online booking form. We offer flexible scheduling to accommodate your needs.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Local SEO Content */}
        <motion.div 
          className="local-seo-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <h2>Premier Dental Care in the Heart of Bhaktapur</h2>
          <p>
            Located in Ram Devi Complex, Suryabinayak, our dental clinic serves as the 
            premier destination for quality dental treatment in Bhaktapur. Our strategic 
            location makes us easily accessible from Katunje, Madhyapur Thimi, and all 
            surrounding areas of Bhaktapur district.
          </p>
          
          <div className="local-highlights">
            <div className="highlight-item">
              <h4>🏆 Most Trusted Dental Clinic</h4>
              <p>Preferred choice of local families for over a decade</p>
            </div>
            <div className="highlight-item">
              <h4>🔬 Advanced Technology</h4>
              <p>Latest dental equipment and modern treatment methods</p>
            </div>
            <div className="highlight-item">
              <h4>👨‍⚕️ Expert Dentists</h4>
              <p>Experienced dental professionals with specialized training</p>
            </div>
            <div className="highlight-item">
              <h4>💰 Affordable Pricing</h4>
              <p>Quality dental care at competitive and transparent rates</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SEOContent;
